import React, { Component } from 'react';
import PropTypes from 'prop-types';

// This was pulled from react-picture and updated to react 16. Consider replacing
// with something like react-media in the future

const getWidth = function () {
  if ('undefined' !== typeof window) {
    return window.innerWidth || document.documentElement.clientWidth;
  }

  return 0;
};

const getDensity = function () {
  if ('undefined' !== typeof window) {
    return window.devicePixelRatio || 1;
  }

  return 1;
};

/** Equivalent to html <img> element
 *
 * <img alt src srcset sizes crossorigin usemap ismap width height>
 */
class BaseImageComponent extends Component {
  constructor (props) {
    super(props);
    this.resizing = false;

    this.state = {
      w: getWidth(),
      x: getDensity()
    };

    this.renderNative = this.renderNative.bind(this);
    this.resizeThrottler = this.resizeThrottler.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  static _buildCandidates (srcset) {
    return srcset.split(',').map(function (srcImg) {
      var stringComponents = srcImg.trim().split(' ');
      var candidate = {
        url: stringComponents[0].trim(),
        w: 0,
        x: 1.0
      };

      for (var i = 1; i < stringComponents.length; i++) {
        var str = stringComponents[i].trim();
        if (-1 !== str.indexOf('w', str.length - 1)) {
          candidate.w = parseInt(str.substring(0, str.length - 1));
        } else if (-1 !== str.indexOf('x', str.length - 1)) {
          candidate.x = parseFloat(str.substring(0, str.length - 1));
        } else {
          console.warn('Invalid parameter passed to Image srcSet: [' + str + '] in ' + srcImg);
        }
      }

      return candidate;
    });
  }

  static __compare (a, b, state, accessorFn) {
    var aDt = accessorFn(a) - state;
    var bDt = accessorFn(b) - state;

    if ((0 === aDt && 0 !== bDt) // a perfectly matches target but b does not
      || (0 > bDt && 0 <= aDt)) { // b is less than target but a is the same or better
      return a;
    }

    if ((0 === bDt && 0 !== aDt) // b perfectly matches target but a does not
      || (0 > aDt && 0 <= bDt)) { // a is less than target but b is the same or better
      return b;
    }

    if (Math.abs(aDt) < Math.abs(bDt)) {
      return a;
    }

    if (Math.abs(bDt) < Math.abs(aDt)) {
      return b;
    }

    return a;
  }

  static _matchImage (candidates, width, density) {
    if (!candidates || 0 === candidates.length) {
      return null;
    }

    return candidates.reduce(function (a, b) {
      if (a.x === b.x) {
        // Both have the same density so attempt to find a better one using width
        if (a.w === b.w) {
          return a; // hey, it came first!
        }

        return BaseImageComponent.__compare(a, b, width, img => img.w);
      }

      return BaseImageComponent.__compare(a, b, density, img => img.x);
    }).url;
  }

  componentDidMount () {
    if ('full' !== this.props.nativeSupport) {
      window.addEventListener('resize', this.resizeThrottler, false);
    }
  }

  componentWillUnmount () {
    if ('full' !== !this.props.nativeSupport) {
      window.removeEventListener('resize', this.resizeThrottler, false);
    }
  }

  render () {
    if (!this.props.srcSet) {
      return null;
    }

    var candidates = BaseImageComponent._buildCandidates(this.props.srcSet);

    if ('full' === this.props.nativeSupport) {
      return this.renderNative(candidates);
    }

    return (
      <img alt={this.props.alt}
        src={BaseImageComponent._matchImage(candidates, getWidth(), getDensity())} {...this.props.extra}/>
    );
  }

  renderNative (candidates) {
    return (
      <img alt={this.props.alt} src={candidates[0].url} srcSet={this.props.srcSet} {...this.props.extra} />
    );
  }

  // Taken from https://developer.mozilla.org/en-US/docs/Web/Events/resize
  resizeThrottler () {
    if (!this.resizing) {
      this.resizing = true;

      if (window && window.requestAnimationFrame) {
        window.requestAnimationFrame(this.onResize);
      } else {
        setTimeout(this.onResize, 66);
      }
    }
  }

  onResize () {
    this.setState({ w: getWidth(), x: getDensity() });
    this.resizing = false;
  }
}

BaseImageComponent.defaultProps = {
  extra: {},
  nativeSupport: 'none'
};

BaseImageComponent.propTypes = {
  srcSet: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  extra: PropTypes.object,
  nativeSupport: PropTypes.string
};


/** Equivalent to html <img> element
 *
 * <img alt src srcset sizes crossorigin usemap ismap width height>
 */
class ImageComponent extends Component {
  constructor (props) {
    super(props);

    // Calculate nativeSupport in constructor so we don't
    // check this everytime the component is mounted despite
    // making it much harder for testing
    var nativeSupport = 'full';
    if ('undefined' !== typeof document) {
      var img = document.createElement('img');
      ('srcset' in img) ? nativeSupport = 'partial' : nativeSupport = 'none';
      ('partial' === nativeSupport && 'sizes' in img) ? nativeSupport = 'full' : nativeSupport = 'partial';
    }

    this.nativeSupport = nativeSupport;
  }

  render () {
    return <BaseImageComponent nativeSupport={this.nativeSupport} {...this.props}/>;
  }
}

ImageComponent.propTypes = {
  srcSet: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  extra: PropTypes.object,
  nativeSupport: PropTypes.string
};

export default ImageComponent;
